import { useEffect, useMemo, useRef, useState } from "react"
import { DatePicker, Select as AntdSelect } from "antd"
import type { Moment } from "moment"
import moment from "moment"
import { TempLayout } from "@/layouts"
import TitleSection from "@/components/common/TitleSection"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import EmptyState from "@/components/common/EmptyState"
import HeTimesheetCard from "./HeTimesheetCard"
import { Skeleton } from "@/components/ui/skeleton"
import { useEquipmentsStore, useHeTimesheetStore } from "@/stores"
import { IHeTimesheet, IHeTimesheetSpvParams } from "@/types"
import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/toast"

const getErrorMessage = (error: unknown): string => {
  const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
  if (apiMessage) return apiMessage
  if (error instanceof Error) return error.message
  return "An unknown error occurred"
}

const { RangePicker } = DatePicker

const HeTimesheetsSpv = () => {
  const getDefaultDateRange = (): [Moment, Moment] => [moment().subtract(89, "days"), moment()]
  const { heTimesheets, fetchHeTimesheetsSupervisor, isLoading, meta, error: storeError } = useHeTimesheetStore()
  const { equipments, fetchEquipments } = useEquipmentsStore()

  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [draftDateRangeFilter, setDraftDateRangeFilter] = useState<[Moment, Moment] | null>(getDefaultDateRange)
  const [draftHullCodesFilter, setDraftHullCodesFilter] = useState<string[]>([])
  const [appliedDateRangeFilter, setAppliedDateRangeFilter] = useState<[Moment, Moment] | null>(getDefaultDateRange)
  const [appliedHullCodesFilter, setAppliedHullCodesFilter] = useState<string[]>([])
  const [allTimesheets, setAllTimesheets] = useState<IHeTimesheet[]>([])

  const prevDataLengthRef = useRef(0)
  const [params, setParams] = useState<IHeTimesheetSpvParams>({
    page: 1,
    limit: 10,
    date_from: "",
    date_to: "",
    hull_codes: "",
  })

  useEffect(() => {
    void fetchEquipments({ page: 1, limit: 100 })
  }, [fetchEquipments])

  useEffect(() => {
    const [dateFrom, dateTo] = appliedDateRangeFilter || []
    const nextParams: IHeTimesheetSpvParams = {
      page: params.page,
      limit: params.limit,
      date_from: dateFrom ? dateFrom.format("YYYY-MM-DD") : "",
      date_to: dateTo ? dateTo.format("YYYY-MM-DD") : "",
      hull_codes: appliedHullCodesFilter.join(","),
    }

    const fetchData = async () => {
      try {
        await fetchHeTimesheetsSupervisor(nextParams)
      } catch (error) {
        toast.error(storeError || getErrorMessage(error))
      }
    }

    void fetchData()
  }, [fetchHeTimesheetsSupervisor, params.page, params.limit, appliedDateRangeFilter, appliedHullCodesFilter, storeError])

  useEffect(() => {
    if (heTimesheets && heTimesheets.length > 0) {
      const mappedNewData: IHeTimesheet[] = heTimesheets

      if (params.page === 1) {
        setAllTimesheets(mappedNewData)
        prevDataLengthRef.current = mappedNewData.length
      } else {
        setAllTimesheets(prev => {
          const existingIds = new Set(prev.map(t => t.record_id))
          const uniqueNewData = mappedNewData.filter(t => !existingIds.has(t.record_id))
          return [...prev, ...uniqueNewData]
        })
        prevDataLengthRef.current = heTimesheets.length
      }
    } else if (params.page === 1 && heTimesheets?.length === 0) {
      setAllTimesheets([])
    }
  }, [heTimesheets, params.page])

  const filteredTimesheets = useMemo(() => {
    let filtered = allTimesheets

    if (statusFilter !== "all") {
      filtered = filtered.filter(timesheet => timesheet.status === statusFilter)
    }

    return filtered
  }, [statusFilter, allTimesheets])

  const handleLoadMore = () => {
    if (meta && params.page && params.page < meta.total_pages && !isLoading) {
      setParams(prev => ({ ...prev, page: (prev.page || 1) + 1 }))
    }
  }

  const handleRefetch = () => {
    setParams(prev => ({ ...prev, page: 1 }))
  }

  const handleClearFilter = () => {
    setDraftDateRangeFilter(null)
    setDraftHullCodesFilter([])
  }

  const handleResetFilter = () => {
    const defaultDateRange = getDefaultDateRange()
    setStatusFilter("all")
    setDraftDateRangeFilter(defaultDateRange)
    setDraftHullCodesFilter([])
    setAppliedDateRangeFilter(defaultDateRange)
    setAppliedHullCodesFilter([])
    setParams(prev => ({ ...prev, page: 1 }))
  }

  const handleSearch = () => {
    setAppliedDateRangeFilter(draftDateRangeFilter)
    setAppliedHullCodesFilter(draftHullCodesFilter)
    setParams(prev => ({ ...prev, page: 1 }))
  }

  return (
    <TempLayout title="Heavy Equipment Timesheet" navNavigateUrl="/home">
      <TitleSection title="HE Timesheet" onRefresh={handleRefetch} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <AntdSelect
          mode="multiple"
          allowClear
          placeholder="Filter equipment codes"
          className="w-full"
          value={draftHullCodesFilter}
          onChange={(values: string[]) => setDraftHullCodesFilter(values)}
          options={(equipments || []).map(eq => ({
            value: eq.asset_number,
            label: eq.asset_number,
          }))}
        />

        <RangePicker
          className="w-full"
          format="YYYY-MM-DD"
          value={draftDateRangeFilter}
          onChange={value => {
            if (!value || value.length !== 2 || !value[0] || !value[1]) {
              setDraftDateRangeFilter(null)
              return
            }
            setDraftDateRangeFilter([value[0], value[1]])
          }}
        />
      </div>

      <div className="flex md:flex-row flex-col justify-between gap-3 mb-4">
        <div className="w-full md:w-[220px]">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="DRAFT">Draft</SelectItem>
              <SelectItem value="POSTED">Posted</SelectItem>
              <SelectItem value="LOCKED">Locked</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-end gap-2 w-full">
          <Button variant="outline" onClick={handleClearFilter}>
            Clear Filter
          </Button>
          <Button  onClick={handleSearch}>
            Search
          </Button>
        </div>
      </div>

      {isLoading && params.page === 1 && allTimesheets.length === 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      ) : filteredTimesheets.length === 0 && !isLoading ? (
        <EmptyState
          title="No Timesheets Found"
          actionLabel={statusFilter !== "all" || draftHullCodesFilter.length > 0 || !!draftDateRangeFilter ? "Reset Filter" : undefined}
          onAction={statusFilter !== "all" || draftHullCodesFilter.length > 0 || !!draftDateRangeFilter ? handleResetFilter : undefined}
        />
      ) : (
        <HeTimesheetCard
          timesheets={filteredTimesheets}
          onLoadMore={handleLoadMore}
          hasMore={meta ? (params.page || 1) < meta.total_pages : false}
          isLoading={isLoading}
        />
      )}
    </TempLayout>
  )
}

export default HeTimesheetsSpv
